import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";

// Pages
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import DashboardIGT from "./pages/DashboardIGT";
import DashboardCabinet from "./pages/DashboardCabinet";
import CabinetsPage from "./pages/CabinetsPage";
import CabinetDetailPage from "./pages/CabinetDetailPage";
import EnterprisesPage from "./pages/EnterprisesPage";
import EnterpriseDetailPage from "./pages/EnterpriseDetailPage";
import BilansPage from "./pages/BilansPage";
import BilanFormPage from "./pages/BilanFormPage";
import BilanDetailPage from "./pages/BilanDetailPage";
import StatisticsPage from "./pages/StatisticsPage";
import NotificationsPage from "./pages/NotificationsPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import AdminsPage from "./pages/AdminsPage";
import ControleursPage from "./pages/ControleursPage";
import EmailHistoryPage from "./pages/EmailHistoryPage";
import CabinetSettingsPage from "./pages/CabinetSettingsPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import CalendarPage from "./pages/CalendarPage";
import SystemSettingsPage from "./pages/SystemSettingsPage";
import VerificationPage from "./pages/VerificationPage";
import QuittanceVerificationPage from "./pages/QuittanceVerificationPage";
import AcceptCGUPage from "./pages/AcceptCGUPage";
import MessagingPage from "./pages/MessagingPage";

// Layout
import AppLayout from "./components/layout/AppLayout";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, mustChangePassword, clearMustChangePassword, mustAcceptCGU, clearMustAcceptCGU } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Force CGU acceptance for cabinet users (BEFORE password change)
  if (mustAcceptCGU && (user.role === "cabinet" || user.role === "collaborateur_cabinet")) {
    return <AcceptCGUPage onAccepted={clearMustAcceptCGU} />;
  }
  
  // Force password change for first login (AFTER CGU acceptance)
  if (mustChangePassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#1B4D3E] to-[#2d7a62] p-4">
        <div className="w-full max-w-md bg-white rounded-xl shadow-2xl p-6">
          <ChangePasswordPage 
            isFirstLogin={true} 
            onSuccess={clearMustChangePassword}
          />
        </div>
      </div>
    );
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Dashboard Router
const DashboardRouter = () => {
  const { user } = useAuth();
  
  if (user?.role === "admin_igt" || user?.role === "super_admin" || user?.role === "admin_igat" || user?.role === "auditeur") {
    return <DashboardIGT />;
  }
  return <DashboardCabinet />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public routes - no authentication required */}
          <Route path="/verification" element={<VerificationPage />} />
          <Route path="/verifier-habilitation" element={<VerificationPage />} />
          <Route path="/verifier" element={<QuittanceVerificationPage />} />
          <Route path="/verifier/:numero" element={<QuittanceVerificationPage />} />
          
          <Route path="/login" element={<LoginPage />} />
          <Route path="/mot-de-passe-oublie" element={<ForgotPasswordPage />} />
          <Route path="/reinitialiser-mot-de-passe" element={<ResetPasswordPage />} />
          
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<DashboardRouter />} />
            
            {/* Admin IGT Routes */}
            <Route
              path="cabinets"
              element={
                <ProtectedRoute allowedRoles={["admin_igt", "super_admin", "admin_igat", "auditeur"]}>
                  <CabinetsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="cabinets/:id"
              element={
                <ProtectedRoute allowedRoles={["admin_igt", "super_admin", "admin_igat", "auditeur"]}>
                  <CabinetDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="administrateurs"
              element={
                <ProtectedRoute allowedRoles={["admin_igt", "super_admin"]}>
                  <AdminsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="controleurs"
              element={
                <ProtectedRoute allowedRoles={["super_admin"]}>
                  <ControleursPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="historique-emails"
              element={
                <ProtectedRoute allowedRoles={["admin_igt", "super_admin", "admin_igat", "cabinet", "collaborateur_cabinet"]}>
                  <EmailHistoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="analytics"
              element={
                <ProtectedRoute allowedRoles={["admin_igt", "super_admin", "admin_igat", "auditeur"]}>
                  <AnalyticsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="calendrier"
              element={
                <ProtectedRoute allowedRoles={["admin_igt", "super_admin", "admin_igat", "auditeur", "cabinet", "collaborateur_cabinet"]}>
                  <CalendarPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="parametres-systeme"
              element={
                <ProtectedRoute allowedRoles={["admin_igt", "super_admin"]}>
                  <SystemSettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="messagerie"
              element={
                <ProtectedRoute allowedRoles={["admin_igt", "super_admin", "admin_igat"]}>
                  <MessagingPage />
                </ProtectedRoute>
              }
            />
            
            {/* Shared Routes */}
            <Route path="entreprises" element={<EnterprisesPage />} />
            <Route path="entreprises/:id" element={<EnterpriseDetailPage />} />
            <Route path="bilans" element={<BilansPage />} />
            <Route path="bilans/nouveau" element={<BilanFormPage />} />
            <Route path="bilans/:id" element={<BilanDetailPage />} />
            <Route path="bilans/:id/modifier" element={<BilanFormPage />} />
            <Route path="statistiques" element={<StatisticsPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="changer-mot-de-passe" element={<ChangePasswordPage />} />
            
            {/* Cabinet Settings */}
            <Route
              path="parametres-cabinet"
              element={
                <ProtectedRoute allowedRoles={["cabinet", "collaborateur_cabinet"]}>
                  <CabinetSettingsPage />
                </ProtectedRoute>
              }
            />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
